/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL THEME CONTEXT v1.0
 * ══════════════════════════════════════════════════════════════════════════════
 * 
 * Context y Provider para gestión centralizada del tema oscuro/claro.
 * Incluye persistencia en localStorage y detección de preferencias del sistema.
 * 
 * Uso:
 *   // En el root de la app
 *   import { ThemeProvider } from './ptel-theme-context';
 *   <ThemeProvider><App /></ThemeProvider>
 * 
 *   // En cualquier componente
 *   import { useTheme } from './ptel-theme-context';
 *   const { theme, isDark, toggleTheme } = useTheme();
 * 
 * ══════════════════════════════════════════════════════════════════════════════
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { colors } from './ptel-design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface Theme {
  // Colores semánticos
  primary: string;
  success: string;
  warning: string;
  error: string;
  
  // Fondos
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  backgroundHover: string;
  
  // Textos
  foreground: string;
  foregroundSecondary: string;
  foregroundMuted: string;
  foregroundSubtle: string;
  foregroundDisabled: string;
  
  // Bordes
  border: string;
  borderLight: string;
}

export interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setDark: (dark: boolean) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMAS PREDEFINIDOS
// ─────────────────────────────────────────────────────────────────────────────

const darkTheme: Theme = {
  primary: colors.primary,
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  background: colors.dark.background,
  backgroundSecondary: colors.dark.backgroundSecondary,
  backgroundTertiary: colors.dark.backgroundTertiary,
  backgroundHover: colors.dark.backgroundHover,
  foreground: colors.dark.foreground,
  foregroundSecondary: colors.dark.foregroundSecondary,
  foregroundMuted: colors.dark.foregroundMuted,
  foregroundSubtle: colors.dark.foregroundSubtle,
  foregroundDisabled: colors.dark.foregroundDisabled,
  border: colors.dark.border,
  borderLight: colors.dark.borderLight,
};

const lightTheme: Theme = {
  primary: colors.primary,
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  background: colors.light.background,
  backgroundSecondary: colors.light.backgroundSecondary,
  backgroundTertiary: colors.light.backgroundTertiary,
  backgroundHover: colors.light.backgroundHover,
  foreground: colors.light.foreground,
  foregroundSecondary: colors.light.foregroundSecondary,
  foregroundMuted: colors.light.foregroundMuted,
  foregroundSubtle: colors.light.foregroundSubtle,
  foregroundDisabled: colors.light.foregroundDisabled,
  border: colors.light.border,
  borderLight: colors.light.borderLight,
};

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ptel-theme';

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────────────────────

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeProviderProps {
  children: ReactNode;
  defaultDark?: boolean;
}

export function ThemeProvider({ children, defaultDark = true }: ThemeProviderProps) {
  // Inicializar desde localStorage o preferencia del sistema
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved !== null) {
        return saved === 'dark';
      }
      // Detectar preferencia del sistema
      if (window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
    }
    return defaultDark;
  });

  // Persistir cambios en localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  // Escuchar cambios en preferencias del sistema
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      // Solo actualizar si no hay preferencia guardada
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === null) {
        setIsDark(e.matches);
      }
    };
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Aplicar clase al body para estilos globales
  useEffect(() => {
    document.body.classList.toggle('dark', isDark);
    document.body.classList.toggle('light', !isDark);
  }, [isDark]);

  const toggleTheme = () => setIsDark(prev => !prev);
  const setDark = (dark: boolean) => setIsDark(dark);

  const theme = useMemo(() => isDark ? darkTheme : lightTheme, [isDark]);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    isDark,
    toggleTheme,
    setDark,
  }), [theme, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────────────────────

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS ADICIONALES
// ─────────────────────────────────────────────────────────────────────────────

export { darkTheme, lightTheme };
// Theme y ThemeContextValue ya exportados arriba como interfaces
